import { ClassKey } from '@const/class.constants';
import { Item } from '@dto/architecture.dto';
import { CustomLinksSearchResult } from '@dto/customLinks.dto';
import { Button, Dropdown } from 'antd';
import { memo, useState } from 'react';
import { XmLink } from 'src/component/link/XmLink';
import XmTagReadOnlySelect from 'src/component/select/XmTagReadOnlySelect';
import { XmTableScope } from 'src/component/table/XmTable';
import CustomLink from 'src/content/tools/cmdb/customLinks/CustomLink';
import { GLOB } from 'src/util/Glob';
import { classDefinitionLabelSearch, ClassKeySearch } from '../constants';

interface Props {
  items: Item[];
  tabSelected: string;
  loading?: boolean;
}


const getRowKey = (item: Item) => item.item_id;

const CUSTOM_LINKS_VISIBLE = 2;

const SearchCustomLinks = ({ links }: { links: CustomLinksSearchResult[]; }) => {
  if (!links.length)
    return null;

  const visible = links.slice(0, CUSTOM_LINKS_VISIBLE);
  const overflow = links.slice(CUSTOM_LINKS_VISIBLE);

  return (
    <div className="xm-search-custom-links">
      {visible.map(link => <CustomLink key={link.id} {...link} />)}
      {overflow.length > 0 && (
        <Dropdown
          trigger={['click', 'hover']}
          placement="bottomRight"
          popupRender={() => (
            <div className="xm-search-custom-links-dropdown">
              {overflow.map(link => <CustomLink key={link.id} {...link} />)}
            </div>
          )}
        >
          <Button size="small" className="xm-search-custom-links-more">+{overflow.length}</Button>
        </Dropdown>
      )}
    </div>
  );
};

const SearchResultsTable = ({ items, tabSelected, loading }: Props) => {
  const [tscope] = useState(() => new XmTableScope<Item>());
  return (
    <div className="text-center">
      <tscope.XmTable
        loading={loading}
        dataSource={items}
        showSorterTooltip={false}
        pagination={false}
        rowKey={getRowKey}
        widthOptions={{ mode: "fill" }}
        fillHeight
      >
        <tscope.Col
          title='Item'
          dataIndex='label'
          minWidth='5em'
          filter={{ field: ['label'] }}
          render={(text: string, item: Item) => (
            <XmLink to={GLOB.menuService.getRouteLink(item.url)}>{text}</XmLink>
          )}
          sorter={(a, b) => GLOB.naturalSort(a.label, b.label)}
          key={'lab'}
        />
        <tscope.Col
          title='Root parent'
          dataIndex={['root_parent', 'label']}
          minWidth='9em'
          filter={{ field: ['root_parent', 'label'] }}
          render={(_: string, item: Item) => {
            return item.root_parent_url && item.root_parent ?
              <XmLink to={GLOB.menuService.getRouteLink(item.root_parent_url)}>{item.root_parent.label}</XmLink> : "";
          }}
          sorter={(a, b) => GLOB.naturalSort(a.root_parent?.label, b.root_parent?.label)}
          key={'root_parent.label'}
        />
        <tscope.Col
          title='Subsystem'
          dataIndex='subsystem'
          minWidth='9em'
          filter={{ field: ['subsystem'] }}
          sorter={(a, b) => GLOB.naturalSort(a.subsystem, b.subsystem)}
          key={'subs'}
        />
        <tscope.Col
          title='Device class'
          dataIndex='hw_type'
          minWidth='9em'
          filter={{ field: ['hw_type'] }}
          sorter={(a, b) => GLOB.naturalSort(a.hw_type, b.hw_type)}
          key={'hw'}
        />
        <tscope.Col
          title='IP Addresses'
          dataIndex={['properties', 'ip_addresses']}
          minWidth='10em'
          filter={{ field: ['properties', 'ip_addresses'] }}
          render={(_, item: Item) => (item.properties?.ip_addresses ? item.properties.ip_addresses?.join(', ') : '')}
          sorter={(a, b) =>
            GLOB.naturalSort(a.properties?.ip_addresses?.join(', '), b.properties?.ip_addresses?.join(', '))
          }
          key={'properties.ip_addresses'}
          overflowModal
        />
        {(tabSelected === classDefinitionLabelSearch[ClassKeySearch.SERVER] || tabSelected === classDefinitionLabelSearch[ClassKey.LAN]) && (
          <tscope.Col
            title='MAC Addresses'
            dataIndex={['properties', 'mac_addresses']}
            minWidth='11em'
            filter={{ field: ['properties', 'mac_addresses'] }}
            render={(_, item: Item) => {
              return item.properties?.mac_addresses?.join(', ')?.length > 20
                ? `${item.properties?.mac_addresses.join(', ').slice(0, 19)} ...`
                : item?.properties?.mac_addresses?.join(', ');
            }
            }
            sorter={(a, b) =>
              GLOB.naturalSort(a.properties?.mac_addresses?.join(', '), b.properties?.mac_addresses?.join(', '))
            }
            key={'properties.mac_addresses'}
            overflowModal
          />
        )}
        {tabSelected === classDefinitionLabelSearch[ClassKey.STORAGE] && (
          <tscope.Col
            title='Volume ID'
            dataIndex={['properties', 'lun_id']}
            minWidth='9em'
            filter={{ field: ['properties', 'lun_id'] }}
            key={'properties.lun_id'}
            render={(_, item: Item) => (item.properties?.lun_id ? item.properties.lun_id?.join(', ') : '')}
            sorter={(a, b) =>
              GLOB.naturalSort(a.properties?.lun_id?.join(', '), b.properties?.lun_id?.join(', '))
            }
          />
        )}
        {items.some(i => i.properties?.wwn?.length) && (
          <tscope.Col
            title='WWN'
            dataIndex={['properties', 'wwn']}
            minWidth='6em'
            filter={{ field: ['properties', 'wwn'] }}
            key={'properties.wwn'}
            render={(_, item: Item) => (item.properties?.wwn ? item.properties.wwn?.join(', ') : '')}
            sorter={(a, b) =>
              GLOB.naturalSort(a.properties?.wwn?.join(', '), b.properties?.wwn?.join(', '))
            }
            overflowModal
          />
        )}
        {(tabSelected === classDefinitionLabelSearch[ClassKey.STORAGE] || tabSelected === classDefinitionLabelSearch[ClassKey.SAN]) && (
          <tscope.Col
            title='Host WWN'
            dataIndex={['properties', 'host_wwns']}
            minWidth='8em'
            maxWidth='22em'
            filter={{ field: ['properties', 'host_wwns'] }}
            key={'properties.host_wwns'}
            render={(_, item: Item) => (item.properties?.host_wwns?.join(', ')?.length > 25
              ? `${item.properties?.host_wwns.join(', ').slice(0, 25)} ...`
              : item.properties?.host_wwns?.join(', '))}
            sorter={(a, b) =>
              GLOB.naturalSort(a.properties?.host_wwns?.join(', '), b.properties?.host_wwns?.join(', '))
            }
            overflowModal
          />
        )}
        {items.some(i => i.class === ClassKey.VIRTUALIZATION && i.properties?.power_state) && (
          <tscope.Col
            title='Power State'
            dataIndex={['properties', 'power_state']}
            minWidth='8em'
            filter={{ field: ['properties', 'power_state'] }}
            key={'properties.power_state'}
            render={(_, item: Item) => item.properties?.power_state ?? ''}
            sorter={(a, b) =>
              GLOB.naturalSort(a.properties?.power_state, b.properties?.power_state)
            }
          />
        )}
        <tscope.Col
          title='Tags'
          dataIndex={['tags_labels', 'length']}
          width={'15em'}
          filter={{ field: ['tags_labels'] }}
          render={(_, item: Item) => (
            <XmTagReadOnlySelect popupMatchSelectWidth={false} tags={item.tags_labels} />
          )}
          sorter={(a, b) => GLOB.naturalSort(a.tags_labels?.join(', '), b.tags_labels?.join(', '))}
          key={'tags'}
          noTooltip
        />
        {items.some(i => i.custom_links?.length) && (
          <tscope.Col
            title='Custom links'
            dataIndex='custom_links'
            className='xm-custom-links-col'
            minWidth='12em'
            filter={{ field: ['custom_links'] }}
            onFilter={(value, item: Item, isCaseSensitive, isRegex) => {
              const labels = (item.custom_links ?? []).map(l => l.label);
              if (!labels.length)
                return false;
              const term = String(value);
              if (isRegex)
                return labels.some(l => new RegExp(term).test(l));
              if (isCaseSensitive)
                return labels.some(l => l.includes(term));
              const needle = term.toLowerCase();
              return labels.some(l => l.toLowerCase().includes(needle));
            }}
            render={(_, item: Item) => <SearchCustomLinks links={item.custom_links ?? []} />}
            key={'custom_links'}
            plainRender
            noTooltip
          />
        )}
      </tscope.XmTable>
    </div>
  );
};

export default memo(SearchResultsTable);
